import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Pencil, Trash2, Plus, LogOut, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../api";
import { ASSETS, CATEGORIES } from "../constants";
import { useLanguage } from "../i18n/LanguageContext";

const EMPTY_VEHICLE = {
  name: "",
  category: "economy",
  price_per_day: "",
  transmission: "Automatic",
  seats: 5,
  engine: "",
  acceleration: "",
  availability: "on_request",
  no_deposit_eligible: false,
  image: "",
  featured: false,
};

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white outline-none focus:border-gold/60";
const labelCls = "mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500";

const AdminPage = () => {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [editing, setEditing] = useState(null);

  const refreshData = () => {
    api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        refreshData();
      })
      .catch(async (err) => {
        if (err.response?.status === 401) {
          try {
            await api.post("/auth/refresh");
            const r = await api.get("/auth/me");
            setUser(r.data);
            refreshData();
            return;
          } catch (e) {
            /* not authenticated */
          }
        }
        setUser(false);
      });
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const r = await api.post("/auth/login", loginForm);
      setUser(r.data);
      refreshData();
    } catch (err) {
      setLoginError(formatApiError(err.response?.data?.detail, t("admin.loginError")));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const saveVehicle = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      price_per_day: editing.price_per_day === "" || editing.price_per_day == null ? null : Number(editing.price_per_day),
      seats: Number(editing.seats) || 5,
      sort_order: editing.sort_order === "" || editing.sort_order == null ? null : Number(editing.sort_order),
      gallery: editing.id ? editing.gallery : editing.image ? [editing.image] : [],
      frames360: [],
    };
    try {
      if (editing.id) {
        await api.put(`/admin/vehicles/${editing.id}`, payload);
      } else {
        await api.post("/admin/vehicles", payload);
      }
      toast.success(t("admin.vehicleSaved"));
      setEditing(null);
      refreshData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try {
      await api.delete(`/admin/vehicles/${id}`);
      toast.success(t("admin.vehicleDeleted"));
      refreshData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setEnquiryStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success(t("admin.statusUpdated"));
      setEnquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#08080A]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/30 border-t-gold" data-testid="admin-loading" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#08080A] px-4">
        <Toaster theme="dark" position="top-center" />
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-sm rounded-3xl border border-gold/15 bg-panel p-8">
          <img src={ASSETS.logo} alt="A&A MOTORS" className="mx-auto h-16 w-16 rounded-full object-cover ring-1 ring-gold/40" />
          <h1 className="mt-5 text-center font-display text-2xl font-bold text-white">{t("admin.login")}</h1>
          <div className="mt-7 space-y-4">
            <div>
              <label htmlFor="admin-email" className={labelCls}>{t("admin.email")}</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" required value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label htmlFor="admin-password" className={labelCls}>{t("admin.password")}</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" required value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} />
            </div>
            {loginError && <p data-testid="admin-login-error" className="text-xs text-red-400">{loginError}</p>}
            <button type="submit" data-testid="admin-login-submit" disabled={loggingIn} className="w-full rounded-full bg-gold py-3.5 text-xs font-bold uppercase tracking-widest text-[#08080A] transition-colors hover:bg-gold-light disabled:opacity-60">
              {loggingIn ? t("admin.signingIn") : t("admin.signIn")}
            </button>
            <Link to="/" data-testid="admin-back-home" className="block text-center text-xs text-slate-500 hover:text-gold">
              {t("admin.back")}
            </Link>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#08080A] text-slate-100">
      <Toaster theme="dark" position="top-center" />
      <header className="sticky top-0 z-30 border-b border-gold/10 bg-[#08080A]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="font-display text-xl font-bold tracking-[0.1em] text-white">
              A<span className="text-gold-gradient">&amp;</span>A <span className="text-gold">MOTORS</span>
            </span>
            <span className="hidden text-white/20 sm:inline">|</span>
            <h1 className="hidden font-display text-lg font-bold text-white sm:block">{t("admin.dashboard")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-back-link" className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs text-slate-300 hover:border-gold/40 hover:text-gold">
              <ArrowLeft className="h-3.5 w-3.5" /> {t("admin.back")}
            </Link>
            <button type="button" data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs text-slate-300 hover:border-red-400/50 hover:text-red-300">
              <LogOut className="h-3.5 w-3.5" /> {t("admin.logout")}
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl gap-2 px-4 pb-3 sm:px-6">
          {["vehicles", "enquiries"].map((key) => (
            <button
              key={key}
              type="button"
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === key ? "bg-gold text-[#08080A]" : "bg-white/[0.04] text-slate-300 hover:text-gold"
              }`}
            >
              {t(`admin.${key}`)}
              {key === "enquiries" && enquiries.filter((x) => x.status === "new").length > 0 && (
                <span className="ms-2 rounded-full bg-red-500 px-2 py-0.5 text-[10px] text-white" data-testid="admin-new-count">
                  {enquiries.filter((x) => x.status === "new").length}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {tab === "vehicles" && (
          <div>
            <div className="mb-6 flex justify-end">
              <button type="button" data-testid="admin-add-vehicle" onClick={() => setEditing({ ...EMPTY_VEHICLE })} className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-[#08080A] hover:bg-gold-light">
                <Plus className="h-4 w-4" /> {t("admin.addVehicle")}
              </button>
            </div>

            {editing && (
              <form data-testid="admin-vehicle-form" onSubmit={saveVehicle} className="mb-8 rounded-2xl border border-gold/20 bg-panel p-6">
                <h2 className="font-display text-xl font-bold text-white">{editing.id ? t("admin.editVehicle") : t("admin.newVehicle")}</h2>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label className={labelCls} htmlFor="v-name">{t("admin.name")}</label>
                    <input id="v-name" data-testid="admin-vehicle-name" required value={editing.name} onChange={(e) => setEditing((v) => ({ ...v, name: e.target.value }))} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="v-category">{t("admin.category")}</label>
                    <select id="v-category" data-testid="admin-vehicle-category" value={editing.category} onChange={(e) => setEditing((v) => ({ ...v, category: e.target.value }))} className={inputCls}>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{t(`categories.${c}`)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="v-price">{t("admin.price")}</label>
                    <input id="v-price" data-testid="admin-vehicle-price" type="number" min="0" value={editing.price_per_day ?? ""} onChange={(e) => setEditing((v) => ({ ...v, price_per_day: e.target.value }))} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="v-sort">Sort order (1 = first)</label>
                    <input id="v-sort" data-testid="admin-vehicle-sort" type="number" min="1" value={editing.sort_order ?? ""} onChange={(e) => setEditing((v) => ({ ...v, sort_order: e.target.value }))} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="v-seats">{t("admin.seats")}</label>
                    <input id="v-seats" data-testid="admin-vehicle-seats" type="number" min="1" max="9" value={editing.seats} onChange={(e) => setEditing((v) => ({ ...v, seats: e.target.value }))} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="v-trans">{t("admin.transmission")}</label>
                    <input id="v-trans" data-testid="admin-vehicle-transmission" value={editing.transmission} onChange={(e) => setEditing((v) => ({ ...v, transmission: e.target.value }))} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="v-avail">{t("admin.availability")}</label>
                    <select id="v-avail" data-testid="admin-vehicle-availability" value={editing.availability} onChange={(e) => setEditing((v) => ({ ...v, availability: e.target.value }))} className={inputCls}>
                      <option value="on_request">{t("fleet.onRequest")}</option>
                      <option value="available">{t("fleet.available")}</option>
                      <option value="unavailable">{t("fleet.unavailable")}</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="v-image">{t("admin.image")}</label>
                    <input id="v-image" data-testid="admin-vehicle-image" dir="ltr" value={editing.image} onChange={(e) => setEditing((v) => ({ ...v, image: e.target.value }))} className={inputCls} />
                  </div>
                  <div className="flex items-end gap-6 pb-1">
                    <label className="flex items-center gap-2 text-xs text-slate-300">
                      <input type="checkbox" data-testid="admin-vehicle-nodeposit" checked={Boolean(editing.no_deposit_eligible)} onChange={(e) => setEditing((v) => ({ ...v, no_deposit_eligible: e.target.checked }))} className="h-4 w-4 accent-[#D4AF37]" />
                      {t("admin.noDeposit")}
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-300">
                      <input type="checkbox" data-testid="admin-vehicle-featured" checked={Boolean(editing.featured)} onChange={(e) => setEditing((v) => ({ ...v, featured: e.target.checked }))} className="h-4 w-4 accent-[#D4AF37]" />
                      {t("admin.featured")}
                    </label>
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <button type="submit" data-testid="admin-vehicle-save" className="rounded-full bg-gold px-7 py-2.5 text-xs font-bold uppercase tracking-wider text-[#08080A] hover:bg-gold-light">
                    {t("admin.save")}
                  </button>
                  <button type="button" data-testid="admin-vehicle-cancel" onClick={() => setEditing(null)} className="rounded-full border border-white/15 px-7 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-gold">
                    {t("admin.cancel")}
                  </button>
                </div>
              </form>
            )}

            <div className="overflow-hidden rounded-2xl border border-white/[0.07]">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-start text-sm">
                  <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-start font-semibold">{t("admin.name")}</th>
                      <th className="px-4 py-3 text-start font-semibold">{t("admin.category")}</th>
                      <th className="px-4 py-3 text-start font-semibold">{t("admin.price")}</th>
                      <th className="px-4 py-3 text-start font-semibold">{t("admin.availability")}</th>
                      <th className="px-4 py-3 text-end font-semibold">{t("admin.edit")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.map((v) => (
                      <tr key={v.id} data-testid={`admin-vehicle-row-${v.id}`} className="border-t border-white/[0.05] hover:bg-white/[0.02]">
                        <td className="px-4 py-3 font-medium text-white">{v.name}</td>
                        <td className="px-4 py-3 text-slate-400">{t(`categories.${v.category}`)}</td>
                        <td className="px-4 py-3 text-gold">{v.price_per_day ? `AED ${v.price_per_day}` : "—"}</td>
                        <td className="px-4 py-3 text-slate-400">{t(`fleet.${v.availability === "on_request" ? "onRequest" : v.availability}`)}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button type="button" data-testid={`admin-edit-${v.id}`} onClick={() => setEditing({ ...v, price_per_day: v.price_per_day ?? "" })} className="rounded-full border border-white/10 p-2 text-slate-300 hover:border-gold/50 hover:text-gold" aria-label={t("admin.edit")}>
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" data-testid={`admin-delete-${v.id}`} onClick={() => deleteVehicle(v.id)} className="rounded-full border border-white/10 p-2 text-slate-300 hover:border-red-400/50 hover:text-red-300" aria-label={t("admin.del")}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-4" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && <p className="rounded-2xl border border-white/[0.07] bg-panel p-10 text-center text-slate-400">{t("admin.empty")}</p>}
            {enquiries.map((enq) => (
              <div key={enq.id} data-testid={`admin-enquiry-${enq.id}`} className="rounded-2xl border border-white/[0.07] bg-panel p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-white">{enq.name}</p>
                    <p className="mt-1 text-xs text-slate-400" dir="ltr">
                      {enq.email} · {enq.phone} · {enq.contact_method}
                    </p>
                    <p className="mt-2 text-sm text-gold">{enq.vehicle_name || t("form.vehicleAny")}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {t("admin.dates")}: {enq.pickup_date} → {enq.return_date} · {enq.handover}
                    </p>
                    {enq.message && <p className="mt-2 text-sm text-slate-400">“{enq.message}”</p>}
                  </div>
                  <select
                    data-testid={`admin-enquiry-status-${enq.id}`}
                    value={enq.status}
                    onChange={(e) => setEnquiryStatus(enq.id, e.target.value)}
                    className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider outline-none ${
                      enq.status === "new" ? "border-gold/50 bg-gold/10 text-gold" : "border-white/10 bg-white/[0.03] text-slate-300"
                    }`}
                  >
                    {["new", "contacted", "confirmed", "closed"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
